import { CIMSColumnsOptions } from 'components/CIMSTable/CIMSColumn/types'
import { AsphaltMixDesign } from '../AsphaltMixDesignsContext/types'
import { useStatusColumn } from '../../../../../hooks/useStatusColumn'
import { HelpMessage } from 'utils/constants'
import { useAsphaltMixDesignsContext } from '../AsphaltMixDesignsContext'
import { alphabeticalCompare } from 'shared'
import { useMixDesignNumberColumn } from '../../../../../hooks/useMixDesignNumberColumn'
import { useExpirationDateColumn } from '../../../../../hooks/useExpirationDateColumn'

export function useColumns(): CIMSColumnsOptions<AsphaltMixDesign> {
  const help = HelpMessage.Materials.Asphalt
  const { edit, refetch } = useAsphaltMixDesignsContext()
  return {
    prominentCustomFields: {
      disableEditWhen: ({ active }) => ({
        alreadyDisabled: false,
        recordInactive: !active,
      }),
      onCheckBoolean: async ({ globalId }, input) => {
        await edit({
          variables: {
            input: { globalId, customFields: { booleanInputs: [input] } },
          },
        })
        refetch()
      },
      onCheckBooleans: async ({ globalId }, input) => {
        await edit({
          variables: {
            input: {
              globalId,
              customFields: { listInputs: { checkboxes: [input] } },
            },
          },
        })
        refetch()
      },
    },
    columns: [
      useStatusColumn(),
      useMixDesignNumberColumn(help.MixID),
      {
        title: 'Targets',
        width: 125,
        align: 'center',
        help: help.TargetsCount,
        render: ({ percentages }) => percentages.length,
        sort: {
          with: 'callback',
          onCompare: (a, b) =>
            alphabeticalCompare(a.percentages, b.percentages, 'length'),
        },
        filter: {
          type: 'default',
          with: 'callback',
          items: (records) =>
            records
              .flatMap(({ percentages }) => percentages)
              .map(({ target: { name, description, id } }) => ({
                text: name,
                value: id,
                tooltip: description,
              })),
          onFilter: (value, { percentages }) =>
            percentages.some(({ target }) => target.id === value),
        },
      },
      {
        title: 'Supplier',
        help: help.Supplier,
        width: 150,
        render: 'supplier',
        sort: { with: 'key', key: 'supplier' },
        filter: { type: 'search', with: 'key', key: 'supplier' },
      },
      useExpirationDateColumn(),
      {
        title: 'Nominal Size',
        width: 150,
        render: 'nominalSize',
        sort: { with: 'key', key: 'nominalSize' },
        filter: { type: 'search', with: 'key', key: 'nominalSize' },
      },
      {
        title: 'Binder Grade',
        render: 'grade',
        width: 150,
        sort: { with: 'key', key: 'grade' },
        filter: { type: 'search', with: 'key', key: 'grade' },
      },
      {
        title: 'Design Binder Content',
        width: 210,
        align: 'right',
        render: 'designBinderContent',
        sort: { with: 'key', key: 'designBinderContent' },
        filter: { type: 'number', with: 'key', key: 'designBinderContent' },
      },
      {
        title: 'VMA',
        width: 150,
        align: 'right',
        render: 'vma',
        sort: { with: 'key', key: 'vma' },
        filter: { type: 'number', with: 'key', key: 'vma' },
      },
      {
        title: 'Theoretical Max Specific Gravity',
        width: 270,
        align: 'right',
        render: 'theoMaxSpecGrav',
        sort: { with: 'key', key: 'theoMaxSpecGrav' },
        filter: { type: 'number', with: 'key', key: 'theoMaxSpecGrav' },
      },
      {
        title: 'Specific Gravity of Aggregate',
        width: 250,
        align: 'right',
        render: 'bulkSpecGravAgg',
        sort: { with: 'key', key: 'bulkSpecGravAgg' },
        filter: { type: 'number', with: 'key', key: 'bulkSpecGravAgg' },
      },
    ],
  }
}
